import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import * as Parse from 'parse';
import * as Sentry from '@sentry/react';
import {AppThunk} from 'state';
import {
  TableOrderClassName,
  TableOrderItem,
  WaiterClassName,
  TableClassName,
  Order,
  OrderClassName,
  TableOrderState,
  PaymentMode,
  MenuItem,
  OrderItemSubOption,
} from './types';
import {formatTableOrder} from 'helpers/tableOrdersFormatter';
import {formatOrder} from 'helpers/orderFormatter';
import {updateTableAvailability} from './tables';
import * as uuid from 'uuid';
import {sum, uniq} from 'lodash';

const TABLE_ORDER_PAGE_SIZE = 10;
interface State {
  error: string | null;
  fetchLoading: boolean;
  deleteLoading: boolean;
  fetchOrdersLoading: boolean;
  updateOrderLoading: boolean;
  addLoading: boolean;
  addSuccessNotification: boolean;
  tableOrders: TableOrderItem[];
  tableOrdersByState: Record<
    Exclude<TableOrderState, 'closed' | 'pending'>,
    {
      tableOrders: TableOrderItem[];
      isLoading: boolean;
      isInitialLoadingDone: boolean;
      totalCount: number;
    }
  >;
  tableOrder: {
    isLoading: boolean;
    tableOrder: TableOrderItem | null;
    error: string | null;
  };
  taxRate: number;
  orderTransfer: {
    isLoading: boolean;
    error: string | null;
  };
}

const initialState: State = {
  error: null,
  fetchLoading: false,
  deleteLoading: false,
  fetchOrdersLoading: false,
  updateOrderLoading: false,
  addLoading: false,
  addSuccessNotification: false,
  tableOrders: [],
  tableOrdersByState: {
    progress: {
      tableOrders: [],
      isLoading: false,
      totalCount: 0,
      isInitialLoadingDone: false,
    },
    completed: {
      tableOrders: [],
      isLoading: false,
      totalCount: 0,
      isInitialLoadingDone: false,
    },
    confirmed: {
      tableOrders: [],
      isLoading: false,
      totalCount: 0,
      isInitialLoadingDone: false,
    },
  },
  tableOrder: {
    isLoading: false,
    tableOrder: null,
    error: null,
  },
  taxRate: 0,
  orderTransfer: {
    isLoading: false,
    error: null,
  },
};
const addTableOrderByState = (
  state: State['tableOrdersByState'],
  tableOrder: TableOrderItem,
) => {
  if (
    ['completed', 'confirmed', 'progress'].includes(tableOrder.state) &&
    state[tableOrder.state].totalCount -
      state[tableOrder.state].tableOrders.length <=
      9
  ) {
    state[tableOrder.state].tableOrders.push(tableOrder);
    state[tableOrder.state].totalCount += 1;
  }
};
const updateTableOrderByState = (
  state: State['tableOrdersByState'],
  tableOrder: Partial<TableOrderItem> & {id: string},
) => {
  const foundTableOrderStateAndIndex = Object.keys(state).reduce(
    (
      found:
        | {
            key: TableOrderState;
            index: number;
          }
        | undefined,
      key,
    ) => {
      const index = state[key as TableOrderState].tableOrders.findIndex(
        (tableOrderItem) => tableOrderItem.id === tableOrder.id,
      );
      if (index > -1) {
        return {
          key: key as TableOrderState,
          index,
        };
      }
      return found;
    },
    undefined,
  );
  if (foundTableOrderStateAndIndex) {
    const key = foundTableOrderStateAndIndex.key;
    const index = foundTableOrderStateAndIndex.index;
    const currentTableOrder = state[key]?.tableOrders[index];
    if (currentTableOrder) {
      if (tableOrder.state !== key && tableOrder.state !== undefined) {
        state[key].tableOrders = state[key].tableOrders.filter(
          (tableOrderItem) => tableOrderItem.id !== tableOrder.id,
        );
        state[key].totalCount -= 1;
        if (['completed', 'confirmed', 'progress'].includes(tableOrder.state)) {
          state[tableOrder.state].tableOrders.push({
            ...currentTableOrder,
            ...tableOrder,
          });
          state[tableOrder.state].totalCount += 1;
        }
      } else {
        state[key].tableOrders[index] = {
          ...currentTableOrder,
          ...tableOrder,
        };
      }
    }
  } else if (
    tableOrder.state &&
    ['completed', 'confirmed', 'progress'].includes(tableOrder.state)
  ) {
    addTableOrderByState(state, tableOrder as TableOrderItem);
  }
};
const deleteTableOrderByState = (
  state: State['tableOrdersByState'],
  tableOrderId: string,
) => {
  Object.keys(state).forEach((key) => {
    const index = state[key as TableOrderState].tableOrders.findIndex(
      (tableOrder) => tableOrder.id === tableOrderId,
    );
    if (index > -1) {
      state[key as TableOrderState].tableOrders.splice(index, 1);
    }
  });
};

const addOrderToTableOrderByState = (
  state: State['tableOrdersByState'],
  tableOrderId: string,
  order: Order,
) => {
  Object.keys(state).forEach((key) => {
    const index = state[key as TableOrderState].tableOrders.findIndex(
      (tableOrder) => tableOrder.id === tableOrderId,
    );
    if (index > -1) {
      state[key as TableOrderState].tableOrders[index].orders.push(order);
    }
  });
};
const updateOrderInTableOrderByState = (
  state: State['tableOrdersByState'],
  tableOrderId: string,
  order: Order,
) => {
  Object.keys(state).forEach((key) => {
    const index = state[key as TableOrderState].tableOrders.findIndex(
      (tableOrder) => tableOrder.id === tableOrderId,
    );
    if (index > -1) {
      const orderIndex = state[key as TableOrderState].tableOrders[
        index
      ].orders.findIndex((orderItem) => orderItem.id === order.id);
      if (orderIndex > -1) {
        state[key as TableOrderState].tableOrders[index].orders[orderIndex] =
          order;
      }
    }
  });
};

const tableOrdersSlice = createSlice({
  name: 'tableOrders',
  initialState,
  reducers: {
    fetchTableOrdersStart(state) {
      state.fetchLoading = true;
      state.error = null;
    },
    fetchTableOrdersSuccess(
      state,
      action: PayloadAction<{taxRate: number; tableOrders: TableOrderItem[]}>,
    ) {
      state.fetchLoading = false;
      state.tableOrders = action.payload.tableOrders;
      state.taxRate = action.payload.taxRate;
    },
    fetchTableOrdersStartByState(
      state,
      action: PayloadAction<TableOrderState>,
    ) {
      state.tableOrdersByState[action.payload].isLoading = true;
      state.error = null;
    },
    fetchTableOrdersSuccessByState(
      state,
      action: PayloadAction<{
        taxRate: number;
        tableOrders: TableOrderItem[];
        state: TableOrderState;
        isFirstFetch: boolean;
      }>,
    ) {
      state.tableOrdersByState[action.payload.state].tableOrders = [
        ...(!action.payload.isFirstFetch
          ? state.tableOrdersByState[action.payload.state].tableOrders
          : []),
        ...action.payload.tableOrders,
      ];
      state.tableOrdersByState[action.payload.state].isLoading = false;
      if (action.payload.isFirstFetch) {
        state.tableOrdersByState[action.payload.state].isInitialLoadingDone =
          true;
      }
      state.taxRate = action.payload.taxRate;
      state.fetchLoading = false;
    },
    fetchTableOrdersSuccessByStateCount(
      state,
      action: PayloadAction<{
        totalCount: number;
        state: TableOrderState;
      }>,
    ) {
      state.tableOrdersByState[action.payload.state].totalCount =
        action.payload.totalCount;
    },
    fetchTableOrdersFailedByState(
      state,
      action: PayloadAction<{
        state: TableOrderState;
        error: string;
      }>,
    ) {
      state.error = action.payload.error;
      state.fetchLoading = false;
    },
    fetchTableOrdersFailed(state, action: PayloadAction<string>) {
      state.fetchLoading = false;
      state.error = action.payload;
    },
    fetchTableOrderStart(state) {
      state.tableOrder.isLoading = true;
      state.tableOrder.error = null;
    },
    fetchTableOrderSuccess(state, action: PayloadAction<TableOrderItem>) {
      state.tableOrder.isLoading = false;
      state.tableOrder.tableOrder = action.payload;
    },
    fetchTableOrderFailed(state, action: PayloadAction<string>) {
      state.tableOrder.isLoading = false;
      state.tableOrder.error = action.payload;
    },
    addTableOrderStart(state) {
      state.addLoading = true;
      state.addSuccessNotification = false;
      state.error = null;
    },
    addTableOrderSuccess(state, action: PayloadAction<TableOrderItem>) {
      state.addLoading = false;
      state.addSuccessNotification = true;
      state.tableOrders.push(action.payload);
      addTableOrderByState(state.tableOrdersByState, action.payload);
    },
    addTableOrderFailed(state, action: PayloadAction<string>) {
      state.addLoading = false;
      state.error = action.payload;
    },
    deleteTableOrderStart(state) {
      state.deleteLoading = true;
      state.error = null;
    },
    deleteTableOrderSuccess(state, action: PayloadAction<string>) {
      state.deleteLoading = false;
      const index = state.tableOrders.findIndex(
        (tableOrder) => tableOrder.id === action.payload,
      );

      if (index > -1) {
        state.tableOrders.splice(index, 1);
      }
      deleteTableOrderByState(state.tableOrdersByState, action.payload);
    },
    deleteTableOrderFailed(state, action: PayloadAction<string>) {
      state.deleteLoading = false;
      state.error = action.payload;
    },
    silentAddTableOrder(state, action: PayloadAction<TableOrderItem>) {
      state.tableOrders.push(action.payload);
      addTableOrderByState(state.tableOrdersByState, action.payload);
    },
    silentUpdateTableOrder(state, action: PayloadAction<TableOrderItem>) {
      const index = state.tableOrders.findIndex(
        (tableOrder) => tableOrder.id === action.payload.id,
      );

      if (index > -1) {
        state.tableOrders[index] = action.payload;
      }
      updateTableOrderByState(state.tableOrdersByState, action.payload);
      if (
        state.tableOrder.tableOrder &&
        action.payload.id === state.tableOrder.tableOrder.id
      ) {
        state.tableOrder.tableOrder = action.payload;
      }
    },
    silentUpdateTableOrderStateAndOrderCount(
      state,
      action: PayloadAction<{
        id: string;
        state: TableOrderState;
        orderCount: number;
      }>,
    ) {
      const index = state.tableOrders.findIndex(
        (tableOrder) => tableOrder.id === action.payload.id,
      );
      if (index > -1) {
        state.tableOrders[index].state = action.payload.state;
        state.tableOrders[index].orderCount = action.payload.orderCount;
        updateTableOrderByState(state.tableOrdersByState, {
          ...state.tableOrders[index],
          state: action.payload.state,
          orderCount: action.payload.orderCount,
        });
      }

      if (
        state.tableOrder.tableOrder &&
        action.payload.id === state.tableOrder.tableOrder.id
      ) {
        state.tableOrder.tableOrder = {
          ...state.tableOrder.tableOrder,
          state: action.payload.state,
          orderCount: action.payload.orderCount,
        };
      }
    },
    fetchOrdersStart(state) {
      state.fetchOrdersLoading = true;
      state.error = null;
    },
    fetchOrdersSuccess(
      state,
      action: PayloadAction<{id: string; orders: Order[]}>,
    ) {
      state.fetchOrdersLoading = false;
      const index = state.tableOrders.findIndex(
        (tableOrder) => tableOrder.id === action.payload.id,
      );

      if (index > -1) {
        state.tableOrders[index].orders = action.payload.orders;
      }
    },
    fetchOrdersFailed(state, action: PayloadAction<string>) {
      state.fetchOrdersLoading = false;
      state.error = action.payload;
    },
    updateOrderStart(state) {
      state.updateOrderLoading = true;
      state.error = null;
    },
    updateOrderSuccess(
      state,
      action: PayloadAction<{id: string; order: Order}>,
    ) {
      state.updateOrderLoading = false;
      // We update both tableOrders and table order because either one of them could be displayed.
      [
        state.tableOrders.find(
          (tableOrder) => tableOrder.id === action.payload.id,
        ),
        state.tableOrder.tableOrder &&
        action.payload.id === state.tableOrder.tableOrder.id
          ? state.tableOrder.tableOrder
          : undefined,
      ].forEach((tableOrder) => {
        if (tableOrder) {
          const index = tableOrder.orders.findIndex(
            (order) => order.id === action.payload.order.id,
          );
          if (index > -1) {
            tableOrder.orders[index] = action.payload.order;
          }
        }
      });
      updateOrderInTableOrderByState(
        state.tableOrdersByState,
        action.payload.id,
        action.payload.order,
      );
    },
    updateOrderFailed(state, action: PayloadAction<string>) {
      state.updateOrderLoading = false;
      state.error = action.payload;
    },
    silentAddOrder(state, action: PayloadAction<{id: string; order: Order}>) {
      const index = state.tableOrders.findIndex(
        (tableOrder) => tableOrder.id === action.payload.id,
      );

      if (index > -1) {
        state.tableOrders[index].orders.push(action.payload.order);
      }
      if (
        state.tableOrder.tableOrder &&
        action.payload.id === state.tableOrder.tableOrder.id
      ) {
        state.tableOrder.tableOrder.orders.push(action.payload.order);
      }
      addOrderToTableOrderByState(
        state.tableOrdersByState,
        action.payload.id,
        action.payload.order,
      );
    },
    silentUpdateOrder(
      state,
      action: PayloadAction<{id: string; order: Order}>,
    ) {
      [
        state.tableOrders.find(
          (tableOrder) => tableOrder.id === action.payload.id,
        ),
        state.tableOrder.tableOrder,
      ].forEach((tableOrder) => {
        if (tableOrder) {
          const index = tableOrder.orders.findIndex(
            (order) => order.id === action.payload.order.id,
          );

          if (index > -1) {
            tableOrder.orders[index] = action.payload.order;
          }
        }
      });
      updateOrderInTableOrderByState(
        state.tableOrdersByState,
        action.payload.id,
        action.payload.order,
      );
    },
    transferOrderStart(state) {
      state.orderTransfer.isLoading = true;
      state.orderTransfer.error = null;
    },
    transferOrderSuccess(state) {
      state.orderTransfer.isLoading = false;
    },
    transferOrderFailed(state, action: PayloadAction<string>) {
      state.orderTransfer.isLoading = false;
      state.orderTransfer.error = action.payload;
    },
    reset(state) {
      state.addSuccessNotification = false;
      state.fetchLoading = false;
      state.updateOrderLoading = false;
      state.addLoading = false;
      Object.keys(state.tableOrdersByState).forEach((tableState) => {
        state.tableOrdersByState[tableState].isLoading = false;
        state.tableOrdersByState[tableState].isInitialLoadingDone = false;
      });
      state.tableOrder.isLoading = false;
      state.tableOrder.error = null;
      state.orderTransfer.isLoading = false;
      state.orderTransfer.error = null;
    },
    dismissNotifications(state) {
      state.addSuccessNotification = false;
    },
    purge() {
      return initialState;
    },
  },
});

export const {
  fetchTableOrdersStart,
  fetchTableOrdersSuccess,
  fetchTableOrdersFailed,
  fetchTableOrdersFailedByState,
  fetchTableOrdersStartByState,
  fetchTableOrdersSuccessByState,
  fetchTableOrdersSuccessByStateCount,
  fetchTableOrderStart,
  fetchTableOrderSuccess,
  fetchTableOrderFailed,
  addTableOrderStart,
  addTableOrderSuccess,
  addTableOrderFailed,
  deleteTableOrderStart,
  deleteTableOrderSuccess,
  deleteTableOrderFailed,
  silentAddTableOrder,
  silentUpdateTableOrder,
  silentUpdateTableOrderStateAndOrderCount,
  fetchOrdersStart,
  fetchOrdersSuccess,
  fetchOrdersFailed,
  updateOrderStart,
  updateOrderSuccess,
  updateOrderFailed,
  silentAddOrder,
  silentUpdateOrder,
  transferOrderStart,
  transferOrderSuccess,
  transferOrderFailed,
  reset,
  dismissNotifications,
  purge,
} = tableOrdersSlice.actions;

export default tableOrdersSlice.reducer;

/**
 * Thunks
 */

export const fetchTableOrders =
  (byState?: TableOrderState, isFirstFetch = false): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchTableOrdersStart());
      if (byState) {
        dispatch(fetchTableOrdersStartByState(byState));
      }

      const currentUser = Parse.User.current()!;
      const restaurant = currentUser.get('restaurant');
      const taxRate = restaurant.get('tax') || 0;

      const completedTableOrdersQuery = new Parse.Query('TableOrder');
      completedTableOrdersQuery.containedIn('state', ['completed']);
      const progressTableOrdersQuery = new Parse.Query('TableOrder');
      progressTableOrdersQuery.containedIn('state', [
        'confirmed',
        'progress',
        'pending',
      ]);
      const limit = TABLE_ORDER_PAGE_SIZE;
      const existingTableOrdersByState = byState
        ? getState().tableOrders.tableOrdersByState[byState]
        : undefined;
      const skip =
        byState && existingTableOrdersByState.isInitialLoadingDone
          ? existingTableOrdersByState.tableOrders.length
          : 0;
      const tableOrdersQuery = byState
        ? new Parse.Query('TableOrder').equalTo('state', byState)
        : Parse.Query.or(completedTableOrdersQuery, progressTableOrdersQuery);
      tableOrdersQuery
        .equalTo('restaurant', restaurant)
        .equalTo('available', true)
        .include([
          'waiter',
          'table',
          'orders',
          'orders.user',
          'orders.discountPointer',
          'orders.refunds',
        ])
        .exists('table')
        .ascending('createdAt');
      let tableOrders: Parse.Object<Parse.Attributes>[];
      if (byState) {
        const results = await Promise.all([
          tableOrdersQuery.limit(limit).skip(skip).find(),
          tableOrdersQuery.count(),
        ]);
        tableOrders = results[0];
        const totalCount = results[1];
        dispatch(
          fetchTableOrdersSuccessByStateCount({
            state: byState,
            totalCount,
          }),
        );
      } else {
        tableOrders = await tableOrdersQuery.find();
      }
      // TODO: Check if removing below is ok
      // // TODO: Change this, without sometime tableOrder.orders is empty but not in sashido
      // await Promise.all(
      //   tableOrders.map((tableOrder) =>
      //     tableOrder.fetchWithInclude([
      //       'orders',
      //       'orders.user',
      //       'orders.discountPointer',
      //     ]),
      //   ),
      // );
      if (byState) {
        dispatch(
          fetchTableOrdersSuccessByState({
            taxRate,
            tableOrders: tableOrders.map(formatTableOrder),
            state: byState,
            isFirstFetch,
          }),
        );
      } else {
        dispatch(
          fetchTableOrdersSuccess({
            taxRate,
            tableOrders: tableOrders.map(formatTableOrder),
          }),
        );
      }
    } catch (error) {
      if (byState) {
        dispatch(
          fetchTableOrdersFailedByState({
            state: byState,
            error: error.toString(),
          }),
        );
      } else {
        dispatch(fetchTableOrdersFailed(error.toString()));
      }
      Sentry.captureException(error);
      // eslint-disable-next-line no-console
      console.error('fetchTableOrders error', error);
    }
  };
interface AddTableOrderPayload {
  tableId: string;
  waiterId: string;
  minGuests: number;
}

export const fetchTableOrderById =
  (id: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(fetchTableOrderStart());

      const query = new Parse.Query(TableOrderClassName);
      query
        .equalTo('objectId', id)
        .include([
          'waiter',
          'table',
          'orders',
          'orders.user',
          'orders.discountPointer',
          'orders.refunds',
        ]);

      const tableOrder = await query.first();
      if (tableOrder) {
        await tableOrder.fetchWithInclude([
          'orders',
          'orders.user',
          'orders.discountPointer',
        ]);
        dispatch(fetchTableOrderSuccess(formatTableOrder(tableOrder)));
      } else {
        dispatch(fetchTableOrderFailed('Unknown table order id'));
      }
    } catch (error) {
      dispatch(fetchTableOrderFailed(error.toString()));
      Sentry.captureException(error);
      // eslint-disable-next-line no-console
      console.error('fetchTableOrderById error', error);
    }
  };
export const fetchAdditionalTableOrderByTableNumber =
  (tableNumber: string): AppThunk =>
  async (dispatch) => {
    try {
      const tableOrderQuery = new Parse.Query('TableOrder');
      tableOrderQuery.equalTo('tableNumber', tableNumber);
      tableOrderQuery.equalTo(
        'restaurant',
        Parse.User.current()!.get('restaurant'),
      );
      tableOrderQuery.containedIn('state', [
        'confirmed',
        'progress',
        'pending',
        'completed',
      ]);
      tableOrderQuery.include([
        'waiter',
        'table',
        'orders',
        'orders.user',
        'orders.discountPointer',
        'orders.refunds',
      ]);
      const tableOrder = await tableOrderQuery.first();
      if (tableOrder) {
        dispatch(silentAddTableOrder(formatTableOrder(tableOrder)));
      }
    } catch (error) {
      Sentry.captureException(error);
      // eslint-disable-next-line no-console
      console.error('fetchAdditionalTableOrderByTableNumber error', error);
    }
  };

export const addTableOrder =
  (formData: AddTableOrderPayload): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(addTableOrderStart());

      const currentUser = Parse.User.current()!;
      const restaurant = await currentUser.get('restaurant').fetch();

      const waiterPointer = new WaiterClassName().set(
        'objectId',
        formData.waiterId,
      );
      const table = new TableClassName().set('objectId', formData.tableId);
      const tableNumber = [
        ...getState().tables.unavailableTables.items,
        ...getState().tables.availableTables.items,
      ].find((table) => table.id === formData.tableId)?.name;

      const tableOrder = await new TableOrderClassName().save({
        restaurant,
        usersId: [],
        waiter: waiterPointer,
        orders: [],
        orderCount: 0,
        available: true,
        minGuests: formData.minGuests,
        state: 'pending',
        tableNumber,
        table,
      });
      dispatch(addTableOrderSuccess(formatTableOrder(tableOrder)));
      dispatch(updateTableAvailability(formData.tableId, false));
    } catch (error) {
      dispatch(addTableOrderFailed(error.toString()));
      Sentry.captureException(error);
      // eslint-disable-next-line no-console
      console.error('addTableOrder error', error);
    }
  };
const getTableOrderStateByOrdersStates = (ordersStates: any[]) => {
  if (!ordersStates.length) {
    return 'pending';
  }

  const statesObject: Record<TableOrderState, boolean> = uniq(
    ordersStates,
  ).reduce((accumulator, currentValue) => {
    accumulator[currentValue] = true;
    return accumulator;
  }, {} as Record<TableOrderState, boolean>);
  if (statesObject.progress) {
    return 'progress';
  } else if (statesObject.confirmed) {
    return 'confirmed';
  } else if (statesObject.pending && !statesObject.completed) {
    return 'pending';
  } else if (statesObject.completed && !statesObject.pending) {
    return 'completed';
  } else {
    return 'progress';
  }
};

export const updateOrderItemAndTableOrderState =
  (
    id: string,
    state: TableOrderState,
    isHotel: boolean,
    orderId?: string,
    itemId?: string,
  ): AppThunk =>
  async (dispatch) => {
    const query = new Parse.Query(TableOrderClassName);
    query.equalTo('objectId', id);
    query.include('orders');
    query.include('table');
    const tableOrder = await query.first();
    const saveOrder = async (order: any) => {
      const orderItems = order.get('order');
      // When closing a table, we get rid of pending orders and mark all others as completed
      // Only save orders that have changed
      let saveNeeded = false;
      orderItems.forEach((item: any) => {
        if (item.itemId === itemId) {
          item.state = state;
          saveNeeded = true;
        }
      });
      if (saveNeeded) {
        order.set('order', orderItems);
        await order.save();
      }
    };

    const closeOrders = async (orders: any[]) => {
      for (const order of orders) {
        const orderItems = order.get('order');
        const filteredItems = orderItems.filter(
          (item: any) => item.state !== 'pending',
        );
        filteredItems.forEach((item: any) => {
          item.state = 'completed';
        });
        order.set('order', filteredItems);
        await order.save();
      }
    };

    const updateOrders = async (tableOrder: any) => {
      const order = orderId
        ? tableOrder.get('orders').find((order: any) => order.id === orderId)
        : null;
      if (order && state !== 'closed') {
        await saveOrder(order);
      } else {
        await closeOrders(tableOrder.get('orders') ?? []);
      }
    };

    const getTableOrderState = (tableOrder: any) => {
      const ordersStates = tableOrder
        .get('orders')
        .map((order: any) =>
          order.get('order').map((item: any) => {
            if (item.itemId === itemId) {
              return state;
            }
            return item.state;
          }),
        )
        .flat();
      return getTableOrderStateByOrdersStates(ordersStates);
    };

    try {
      if (tableOrder) {
        if (state === 'closed') {
          tableOrder.set('state', 'closed');
          if (!isHotel) {
            dispatch(updateTableAvailability(tableOrder.get('table').id, true));
          }
        } else {
          tableOrder.set('state', getTableOrderState(tableOrder));
        }
        updateOrders(tableOrder);
        dispatch(silentUpdateTableOrder(formatTableOrder(tableOrder)));
        await tableOrder.save();
      }
    } catch (error) {
      Sentry.captureException(error);
      // eslint-disable-next-line no-console
      console.error('saveOrders error', error);
    }
  };

export const deleteTableOrder =
  (id: string, isHotel: boolean): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(deleteTableOrderStart());

      const query = new Parse.Query(TableOrderClassName);
      const tableOrder = await query
        .include(['orders', 'orders.refunds'])
        .get(id);
      if (tableOrder) {
        await Promise.all(
          tableOrder.get('orders').map((order: any) =>
            order
              .get('refunds')
              .flat()
              .map((refund: any) => refund.destroy()),
          ),
        );
        await Promise.all(
          tableOrder.get('orders').map((order: any) => order.destroy()),
        );
        await tableOrder.destroy();
      }

      dispatch(deleteTableOrderSuccess(id));
      if (!isHotel) {
        dispatch(updateTableAvailability(tableOrder.get('table').id, true));
      }
    } catch (error) {
      dispatch(deleteTableOrderFailed(error.toString()));
      Sentry.captureException(error);
      // eslint-disable-next-line no-console
      console.error('deleteTableOrder error', error);
    }
  };

export const fetchOrders =
  (tableOrderId: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(fetchOrdersStart());

      const currentUser = Parse.User.current()!;
      const restaurant = await currentUser.get('restaurant').fetch();

      const tableOrderPointer = new TableOrderClassName().set(
        'objectId',
        tableOrderId,
      );

      const query = new Parse.Query(OrderClassName);
      query
        .equalTo('restaurant', restaurant)
        .equalTo('tableOrder', tableOrderPointer)
        .include(['user', 'discountPointer', 'refunds'])
        .ascending('createdAt');

      const orders = await query.find();

      dispatch(
        fetchOrdersSuccess({id: tableOrderId, orders: orders.map(formatOrder)}),
      );
    } catch (error) {
      dispatch(fetchOrdersFailed(error.toString()));
      Sentry.captureException(error);
      // eslint-disable-next-line no-console
      console.error('fetchOrders error', error);
    }
  };

interface UpdateOrderPayload {
  paid?: boolean;
  paymentMode?: PaymentMode;
}

export const updateOrder =
  (orderId: string, formData: UpdateOrderPayload): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(updateOrderStart());

      const query = new Parse.Query('Order');
      query.equalTo('objectId', orderId).include(['user', 'discountPointer']);

      const order = await query.first();

      if (order) {
        if (formData.paid !== undefined) {
          order.set('paid', formData.paid);
        }
        if (formData.paymentMode !== undefined) {
          order.set('paymentMode', formData.paymentMode);
        }
        await order.save();
        dispatch(
          updateOrderSuccess({
            id: order.get('tableOrder').id,
            order: formatOrder(order),
          }),
        );
      } else {
        dispatch(updateOrderFailed('Unknown order id'));
      }
    } catch (error) {
      dispatch(updateOrderFailed(error.toString()));
      Sentry.captureException(error);
      // eslint-disable-next-line no-console
      console.error('updateOrder error', error);
    }
  };

export const orderItems =
  (
    orderId: string,
    menuItem: MenuItem,
    quantity: number,
    tableOrder: TableOrderItem,
    subOptions?: OrderItemSubOption[],
  ): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(updateOrderStart());

      const currentUser = Parse.User.current()!;
      const restaurant = await currentUser.get('restaurant').fetch();

      const query = new Parse.Query('Order');
      query.equalTo('objectId', orderId).include(['user', 'discountPointer']);

      const order = await query.first();

      const tableOrderQuery = new Parse.Query('TableOrder');
      tableOrderQuery.equalTo('objectId', tableOrder.id);

      const tableOrderFromParse = await tableOrderQuery.first();

      if (order && tableOrderFromParse) {
        tableOrderFromParse.increment('orderCount');
        // If the table order was not yet validated by the client, we automatically validate it (state = progress) when a waiter adds an item
        if (tableOrderFromParse.get('state') === 'pending') {
          tableOrderFromParse.set('state', 'progress');
        }
        await tableOrderFromParse.save();
        const orderNumber = tableOrderFromParse.get('orderCount');
        const orderItems = order.get('order');
        orderItems.push({
          item: {
            ...restaurant
              .get('menu')
              .find((category: any) => category.id === menuItem.category)
              .menu.find((item: any) => item.id === menuItem.id),
            subOptions,
          },
          number: quantity,
          comment: '',
          state: 'progress',
          orderNumber: orderNumber,
          itemId: uuid.v4(),
        });
        order.set('order', orderItems);
        await order.save();
        dispatch(
          updateOrderSuccess({
            id: order.get('tableOrder').id,
            order: formatOrder(order),
          }),
        );
      } else {
        dispatch(updateOrderFailed('Unknown order id'));
      }
    } catch (error) {
      dispatch(updateOrderFailed(error.toString()));
      Sentry.captureException(error);
      // eslint-disable-next-line no-console
      console.error('orderItems error', error);
    }
  };
export const addDiscount =
  (amount: number, order: Order): AppThunk =>
  async (dispatch) => {
    const query = new Parse.Query('Order');
    query.equalTo('objectId', order.id);
    const orderFromParse = await query.first();
    if (orderFromParse) {
      orderFromParse.set('discountValue', amount);
      await orderFromParse.save();
      dispatch(
        updateOrderSuccess({
          id: orderFromParse.get('tableOrder').id,
          order: formatOrder(orderFromParse),
        }),
      );
    } else {
      dispatch(updateOrderFailed('Unknown order.id'));
    }
  };

export const addRefund =
  (
    amount: number,
    taxRate: number,
    tip: number,
    reason: string,
    order: Order,
    itemId: string,
    quantity: number,
  ): AppThunk =>
  async (dispatch) => {
    const query = new Parse.Query('Order');
    query.equalTo('objectId', order.id).include(['user', 'discountPointer']);

    const orderFromParse = await query.first();

    if (orderFromParse) {
      const paidByCard =
        orderFromParse.get('paid') &&
        orderFromParse.get('paymentMode') === 'card';

      const totalAmount = amount + amount * taxRate + amount * tip;
      try {
        if (paidByCard) {
          const transactionId = orderFromParse.get('transactionId');
          if (!transactionId) new Error('No transaction id to refund...');
          await Parse.Cloud.run('refund', {
            transactionId,
            totalAmount,
          });
        }
        const Refund = Parse.Object.extend('Refund');
        const refund = new Refund();

        refund.set('amount', amount);
        refund.set('taxRate', taxRate);
        refund.set('tip', tip);
        refund.set('totalAmount', totalAmount);
        refund.set('reason', reason);
        refund.set('order', orderFromParse);
        refund.set('itemId', itemId);
        refund.set('itemQuantity', quantity);

        dispatch(updateOrderStart());
        await refund.save();
        const refunds = orderFromParse.get('refunds') || [];
        refunds.push(refund);
        orderFromParse.set('refunds', refunds);

        await orderFromParse.save();

        dispatch(
          updateOrderSuccess({
            id: orderFromParse.get('tableOrder').id,
            order: formatOrder(orderFromParse),
          }),
        );
      } catch (error) {
        dispatch(updateOrderFailed(error.toString()));
        Sentry.captureException(error);
        // eslint-disable-next-line no-console
        console.error('addRefund error', error);
      }
    } else {
      dispatch(updateOrderFailed('Unknown order.id'));
    }
  };

export const transferOrderToOtherTableOrder = ({
  sourceTableOrderId,
  destinationTableId,
  ordersIds,
}: {
  sourceTableOrderId: string;
  destinationTableId: string;
  ordersIds: string[];
}): AppThunk => {
  return async (dispatch) => {
    try {
      dispatch(transferOrderStart());
      const sourceTableOrderQuery = new Parse.Query('TableOrder');
      const includedFields = [
        'waiter',
        'table',
        'orders',
        'orders.user',
        'orders.discountPointer',
        'orders.refunds',
      ];
      const sourceTableOrder = await sourceTableOrderQuery
        .equalTo('objectId', sourceTableOrderId)
        .first();
      await sourceTableOrder?.fetchWithInclude(includedFields);
      const destinationTableOrderQuery = new Parse.Query('TableOrder');
      const table = new TableClassName().set('objectId', destinationTableId);
      const destinationTableOrder = await destinationTableOrderQuery
        .equalTo('table', table)
        .notEqualTo('state', 'closed')
        .first();
      await destinationTableOrder?.fetchWithInclude(includedFields);
      if (!sourceTableOrder || !destinationTableOrder) {
        return;
      }
      const sourceTableOrderOrders = sourceTableOrder.get('orders');
      const destinationTableOrderOrders = destinationTableOrder.get('orders');
      const ordersThatCanBeTransferred = sourceTableOrderOrders.filter(
        (order: any) => ordersIds.includes(order.id),
      );
      const updatedSourceTableOrderOrders = sourceTableOrderOrders.filter(
        (order: any) => !ordersIds.includes(order.id),
      );
      sourceTableOrder.set('orders', updatedSourceTableOrderOrders);
      sourceTableOrder.set(
        'orderCount',
        sum(
          updatedSourceTableOrderOrders.map(
            (order: any) => order.get('order').length || 0,
          ) || [],
        ),
      );
      sourceTableOrder.set(
        'usersId',
        updatedSourceTableOrderOrders.map((order: any) => order.get('user').id),
      );
      sourceTableOrder.set(
        'state',
        getTableOrderStateByOrdersStates(
          updatedSourceTableOrderOrders.map((order: any) => order.get('state')),
        ),
      );
      ordersThatCanBeTransferred.forEach((order: any) => {
        order.set('tableOrder', destinationTableOrder);
      });
      const updatedDestinationTableOrderOrders =
        destinationTableOrderOrders.concat(ordersThatCanBeTransferred);
      destinationTableOrder.set('orders', updatedDestinationTableOrderOrders);
      destinationTableOrder.set(
        'orderCount',
        sum(
          updatedDestinationTableOrderOrders.map(
            (order: any) => order.get('order').length || 0,
          ) || [],
        ),
      );
      destinationTableOrder.set(
        'usersId',
        destinationTableOrder
          .get('usersId')
          .concat(
            sourceTableOrderOrders
              .filter((order: any) => ordersIds.includes(order.id))
              .map((order: any) => order.get('user').id),
          ),
      );
      destinationTableOrder.set(
        'state',
        getTableOrderStateByOrdersStates(
          updatedDestinationTableOrderOrders.map((order: any) =>
            order.get('state'),
          ),
        ),
      );
      await sourceTableOrder.save();
      await destinationTableOrder.save();
      await Promise.all(
        ordersThatCanBeTransferred.map((order: any) => order.save()),
      );
      const updatedSourceTableOrder = await sourceTableOrderQuery
        .equalTo('objectId', sourceTableOrderId)
        .first();
      await updatedSourceTableOrder?.fetchWithInclude(includedFields);
      dispatch(
        silentUpdateTableOrder(formatTableOrder(updatedSourceTableOrder)),
      );
      dispatch(silentUpdateTableOrder(formatTableOrder(destinationTableOrder)));
      dispatch(transferOrderSuccess());
    } catch (error) {
      Sentry.captureException(error);
      // eslint-disable-next-line no-console
      console.error('transferCustomerToOtherTableOrder error', error);
      dispatch(transferOrderFailed(error.toString()));
    }
  };
};
